/**
 * shared/ui/empty-state/EmptyState — Reusable empty-state component.
 *
 * Renders a centred block with an optional icon, a title, an optional
 * description, and an optional action button.  All styling uses theme tokens
 * (text-foreground, text-muted-foreground, etc.) so it works in both light
 * and dark mode.
 *
 * Usage:
 *   <EmptyState
 *     icon={<Building2 className="h-10 w-10" />}
 *     title={t('list.empty')}
 *     description={t('list.emptyDescription')}
 *     action={{ label: t('list.retry'), onClick: () => void refetch() }}
 *     data-testid="lifts-table-empty"
 *   />
 *
 * FSD: shared/ui — no business logic, no API calls.
 */

import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';

export interface EmptyStateAction {
  label: string;
  onClick: () => void;
}

export interface EmptyStateProps {
  /** Lucide icon or illustration rendered above the title. */
  icon?: ReactNode;
  /** Primary heading text — always required. */
  title: string;
  /** Supporting description text below the title. */
  description?: string;
  /** Optional action button (e.g. retry, navigate). */
  action?: EmptyStateAction;
  className?: string;
  'data-testid'?: string;
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
  'data-testid': testId,
}: EmptyStateProps) {
  return (
    <div
      className={cn('flex flex-col items-center gap-2 py-8 text-center', className)}
      data-testid={testId}
    >
      {icon && (
        <div className="mb-1 text-muted-foreground" aria-hidden="true">
          {icon}
        </div>
      )}
      <p className="text-sm font-medium text-foreground">{title}</p>
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
      {action && (
        <Button
          variant="outline"
          size="sm"
          type="button"
          onClick={action.onClick}
          className="mt-2"
          data-testid="empty-state-action"
        >
          {action.label}
        </Button>
      )}
    </div>
  );
}
